import { FC, Fragment, PropsWithChildren, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import appConfig from 'app-config';
import Modal from 'src/components/Modal';
import SeoKeywords from 'src/components/SeoKeywords';
import SplashScreen from 'src/components/SplashScreen';
import Toast from 'src/components/Toast';
import WebClientWrapper from 'src/components/Wrapper/WebClientWrapper';
import WebviewWrapper from 'src/components/Wrapper/WebviewWrapper';
import { useInitEventGateway } from 'src/hooks/useInitEventGateway';
import useSession from 'src/hooks/useSession';
import useUrlFragmentWebLink from 'src/hooks/useUrlFragmentWebLink';
import { setDTIDAtom, userDataAtom } from 'src/stores/auth/atoms';
import { closeAllModalAtom } from 'src/stores/modal/atoms';
import { pageReadyAtom } from 'src/stores/splash/atom';
import { PageMetas } from 'src/types/meta';
import { Container, RootStyle } from './style';
const OneTrustLoader = dynamic(() => import('src/components/OneTrustLoader'), {
  ssr: false
});
type Props = PageMetas;
const Layout: FC<PropsWithChildren<Props>> = ({
  children,
  metas
}) => {
  const router = useRouter();
  const closeAllModal = useSetAtom(closeAllModalAtom);
  const setPageReady = useSetAtom(pageReadyAtom);
  const userData = useAtomValue(userDataAtom);
  useUrlFragmentWebLink();
  const [isClient, setIsClient] = useState(false);
  const isWebview = isClient && !!window.AzarJs;
  useEffect(() => {
    setIsClient(true);
  }, []);
  const Wrapper = isClient ? isWebview ? WebviewWrapper : WebClientWrapper : Fragment;
  useSession();
  useInitEventGateway();
  const setDTID = useSetAtom(setDTIDAtom);
  useEffect(() => {
    setDTID();
  }, [setDTID]);
  useEffect(() => {
    setPageReady(true);
    return () => setPageReady(false);
  }, [setPageReady]);
  useEffect(() => {
    Sentry.setUser({
      id: userData?.userId
    });
  }, [userData?.userId]);
  useEffect(() => {
    const handleRouteChange = () => {
      closeAllModal();
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [closeAllModal, router.events]);
  return <Wrapper data-sentry-element="Wrapper" data-sentry-component="Layout" data-sentry-source-file="index.tsx">
      <OneTrustLoader data-sentry-element="OneTrustLoader" data-sentry-source-file="index.tsx">
        <RootStyle data-sentry-element="RootStyle" data-sentry-source-file="index.tsx">
          <SeoKeywords metas={metas} data-sentry-element="SeoKeywords" data-sentry-source-file="index.tsx" />
          <Container isWebview={isWebview} data-sentry-element="Container" data-sentry-source-file="index.tsx">{children}</Container>
          <Toast data-sentry-element="Toast" data-sentry-source-file="index.tsx" />
          <Modal data-sentry-element="Modal" data-sentry-source-file="index.tsx" />
          {appConfig.serviceCode === 'azar' && <SplashScreen />}
        </RootStyle>
      </OneTrustLoader>
    </Wrapper>;
};
export default Layout;